import * as React from 'react';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import PersonnelToast from './PersonnelToast';
import useFetch from 'use-http';
import _ from 'lodash';
import TSDILandman from 'src/models/personnel/TSDILandman';
import actions from 'src/actions';
import DialogList from 'src/dialogs';
import AddPersonnelDialog from '../dialogs/AddPersonnelDialog';
import Select, { StylesConfig } from 'react-select';
import CondensedPersonnel from 'src/models/personnel/CondensedPersonnel';

interface IPersonnelSearchInheritedProps {
    showPersonnelData?: (selected: TSDILandman) => void;
    setIsCollapsed?: (isCollpased: boolean) => void;
    toggleAdvancedQuickFind?: () => void;
    toggleBenefitSearch?: () => void;
    toggleYearMatrix?: () => void;
    toggleBenefitsDashboard?: () => void;
    toggleInsuranceDashboard?: () => void;
}

interface IOptionType {
    value: number;
    label: string;
    tsdNo: number;
    firstName: string;
    lastName: string;
}

type IPersonnelSearchProps = IPersonnelSearchInheritedProps;

const PersonnelSearch: React.FC<IPersonnelSearchProps> = (props) => {
    const { register, errors } = useForm();
    const { get, response } = useFetch();
    const [isLoadingError, setIsLoadingError] = useState<boolean>(false); // tracked to display user facing error message if personnel is not loaded
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);

    const [isUserSearchingPersonnel, setIsUserSearchingPersonnel] = useState<boolean>(false); // true if the search text is not empty
    const [personnelSearchResults, setPersonnelSearchResults] = useState<IOptionType[]>(undefined);
    const latestSearchId = useRef(0);

    const dispatch = useDispatch();
    let selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel); // Personnel selected by the user

    const customStyles: StylesConfig<IOptionType, false> = {
        placeholder: (provided) => ({
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '30px',    // Set a smaller minHeight
            height: '30px',       // Force a smaller height
            padding: '0px',       // Remove internal padding to make it smaller
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0px 8px',   // Adjust padding to make the height smaller
            height: '30px',       // Force height for the container
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',        // Ensure no margins are added
            padding: '0px',       // Ensure no padding is added
            whiteSpace: 'nowrap'  // Ensure that the content doesn't wrap
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '30px',       // Force the height of the dropdown indicators
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '0px',       // Remove extra padding from the dropdown arrow
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: '0px',       // Remove extra padding from the clear indicator
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 999
        }),
    };

    // SelectedPersonnel will change when the user clicks on a user or on initial render, if url contains an id
    useEffect(() => {
        if (selectedPersonnel) {
            console.log('====> SelectedPersonnel = ', selectedPersonnel.fName, selectedPersonnel.lName);
        }
    }, [selectedPersonnel]);

    // Picks up a pending employee left by another page (e.g. Year Matrix double-click) via
    // localStorage - same pattern ClientProjectSearch.tsx uses for client/project selection.
    // PersonnelSearch is remounted fresh on every personnel page navigation, so this runs once
    // right when the target page (e.g. Employee Benefits) actually mounts, going through the same
    // setSelectedPersonnel flow a normal manual search selection uses rather than a one-off
    // dispatch from the page that navigated here.
    useEffect(() => {
        const pendingTsdNo = localStorage.getItem('personnelPendingTsdNo');
        if (pendingTsdNo) {
            localStorage.removeItem('personnelPendingTsdNo');
            const tsdNo = parseInt(pendingTsdNo, 10);
            if (!isNaN(tsdNo)) {
                selectByTsdNo(tsdNo);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectByTsdNo = async (tsdNo: number) => {
        let result = await get(`v1.0/landmen/personnel/${tsdNo}/get-by-tsd-no`);

        if (response.ok && result) {
            setSelectedPersonnel(result);
        } else {
            console.log('unable to find personnel by tsd_no');
        }
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const addPersonnel = () => {
        dispatch(actions.layout.showDialog(DialogList.ADD_PERSONNEL));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

            setIsLoadingError(false);
        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
            setIsLoadingError(true);
        }
    };

    const handleSelectItem = async (id: number) => {
        let result = await get(`v1.0/landmen/personnel/${id}/get-by-lm-no`);

        if (response.ok && result) {
            setSelectedPersonnel(result);
        } else {
            console.log('unable to find personnel');
        }
    };

    const search = _.debounce(async (term: string) => {
        if (!term) {
            ++latestSearchId.current;
            setPersonnelSearchResults(undefined);
            return;
        }

        if (term.length < 3) {
            ++latestSearchId.current;
            setPersonnelSearchResults(undefined);
            return;
        }

        const requestId = ++latestSearchId.current;
        let results = await get(`v1.0/landmen/personnel/search-by-category/?term=${encodeURIComponent(term)}`);

        if (requestId !== latestSearchId.current) {
            return;
        }

        if (response.ok && results) {
            setPersonnelSearchResults(results.map((ct: CondensedPersonnel) => ({
                value: ct.lmNo,
                label: `${ct.tsdNo} - ${ct.firstName} ${ct.lastName}`,
                tsdNo: ct.tsdNo,
                firstName: ct.firstName,
                lastName: ct.lastName,
            })));
        } else {
            showToast(`Unable to find landman with provided term: ${term}`, `rgba(255,0,0,0.5)`);
        }
    }, 450);

    const handlePersonnelListSearchChanged = (input: string) => {
        search(input);
    };

    if (isLoadingError) {
        return (
            <div style={{ textAlign: 'center' }}>
                <h2>Unable to load personnel</h2>
            </div>
        );
    } else if (window.location.pathname.indexOf('/employee-benefits') >= 0 ||
        window.location.pathname.indexOf('/insurance-history') >= 0 ||
        window.location.pathname.indexOf('/benefits-history') >= 0 ||
        window.location.pathname.indexOf('/rates') >= 0 ||
        window.location.pathname.indexOf('/benefit-tables') >= 0) {
        return (
            <>
                <div>
                    {toast && (
                        <PersonnelToast
                            message={toast.message}
                            color={toast.color}
                            onClose={() => setToast(null)}
                            persistent={toast.persistent}
                        />
                    )}
                </div>
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={4}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel>
                                <div style={{ display: 'flex', padding: '0', margin: '0', alignItems: 'center' }}>
                                    <i
                                        className="fas fa-search"
                                        style={{ color: '#6393C1', position: 'relative', marginRight: '1rem', fontSize: '1em' }}
                                    />
                                    <div style={{ width: '50%' }}>
                                        <Select
                                            className="project-billing personnel-search-list"
                                            name="personnelSearchResults"
                                            placeholder={
                                                selectedPersonnel?.tsdNo ?
                                                    '(' + selectedPersonnel?.tsdNo + ') ' + (selectedPersonnel?.akaName ?
                                                        selectedPersonnel?.akaName :
                                                        selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName) :
                                                    'Select Personnel'
                                            }
                                            options={personnelSearchResults}
                                            onChange={(e: IOptionType) => handleSelectItem(e.value)}
                                            onMenuOpen={() => setIsUserSearchingPersonnel(true)}
                                            onMenuClose={() => { setIsUserSearchingPersonnel(false); setPersonnelSearchResults(undefined); }}
                                            onBlur={() => { setIsUserSearchingPersonnel(false); setPersonnelSearchResults(undefined); }}
                                            onInputChange={handlePersonnelListSearchChanged}
                                            register={register}
                                            errors={errors}
                                            menuIsOpen={isUserSearchingPersonnel}
                                            isSearchable={true}
                                            styles={customStyles}
                                            formatOptionLabel={(option: IOptionType) => (
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ minWidth: '8ch', flexShrink: 0 }}>({option.tsdNo})</span>
                                                    <span>{option.firstName} {option.lastName}</span>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', padding: '0', margin: '0', marginLeft: 'auto', alignItems: 'center' }}>
                                        <Button
                                            text="Advanced Find"
                                            className="blue-hover-button"
                                            style={{ width: 'auto', height: '3rem', verticalAlign: 'middle', marginLeft: '0.25rem', borderRadius: '5px', whiteSpace: 'nowrap' }}
                                            onClick={() => props.toggleAdvancedQuickFind()}
                                        />
                                    </div>
                                </div>
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
                <AddPersonnelDialog
                    dialogName={DialogList.ADD_PERSONNEL}
                    hideDialog={hideDialog}
                    setSelectedPersonnel={setSelectedPersonnel}
                />
            </>
        );
    } else {
        return (
            <>
                <div>
                    {toast && (
                        <PersonnelToast
                            message={toast.message}
                            color={toast.color}
                            onClose={() => setToast(null)}
                            persistent={toast.persistent}
                        />
                    )}
                </div>
                <Page style={{ justifyContent: 'center' }}>
                    <Page.Section width={6}>
                        <Panel backgroundColor="#D3D3D3">
                            <Panel>
                                <div style={{ display: 'flex', padding: '0', margin: '0', alignItems: 'center' }}>
                                    <i
                                        className="fas fa-search"
                                        style={{ color: '#6393C1', position: 'relative', marginRight: '1rem', fontSize: '1em' }}
                                    />
                                    <div style={{ width: '50%' }}>
                                        <Select
                                            className="project-billing personnel-search-list"
                                            name="personnelSearchResults"
                                            placeholder={
                                                selectedPersonnel?.tsdNo ?
                                                    '(' + selectedPersonnel?.tsdNo + ') ' + (selectedPersonnel?.akaName ?
                                                        selectedPersonnel?.akaName :
                                                        selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName) :
                                                    'Select Personnel'
                                            } 
                                            options={personnelSearchResults}
                                            onChange={(e: IOptionType) => handleSelectItem(e.value)}
                                            onMenuOpen={() => setIsUserSearchingPersonnel(true)}
                                            onMenuClose={() => { setIsUserSearchingPersonnel(false); setPersonnelSearchResults(undefined); }}
                                            onBlur={() => { setIsUserSearchingPersonnel(false); setPersonnelSearchResults(undefined); }}
                                            onInputChange={handlePersonnelListSearchChanged}
                                            register={register}
                                            errors={errors}
                                            menuIsOpen={isUserSearchingPersonnel}
                                            isSearchable={true}
                                            styles={customStyles}
                                            formatOptionLabel={(option: IOptionType) => (
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ minWidth: '8ch', flexShrink: 0 }}>({option.tsdNo})</span>
                                                    <span>{option.firstName} {option.lastName}</span>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div className={'form-row'} style={{ display: 'flex', padding: '0', margin: '0', marginLeft: 'auto', alignItems: 'center' }}>
                                        <Button
                                            text="Advanced Find"
                                            className="blue-hover-button"
                                            style={{ width: 'auto', height: '3rem', verticalAlign: 'middle', marginLeft: '0.25rem', borderRadius: '5px', whiteSpace: 'nowrap' }}
                                            onClick={() => props.toggleAdvancedQuickFind()}
                                        />
                                        <div style={{ textAlign: 'center' }}>
                                            <Button
                                                className="blue-hover-button"
                                                text="Add Personnel"
                                                onClick={() => addPersonnel()}
                                                style={{ width: 'auto', height: '3rem', verticalAlign: 'middle', marginLeft: '0.25rem', borderRadius: '5px', whiteSpace: 'nowrap' }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Panel>
                        </Panel>
                    </Page.Section>
                </Page>
                <AddPersonnelDialog
                    dialogName={DialogList.ADD_PERSONNEL}
                    hideDialog={hideDialog}
                    setSelectedPersonnel={setSelectedPersonnel}
                />
            </>
        );
    }
};

export default PersonnelSearch;


// WEBPACK FOOTER //
// ./src/components/personnel/components/PersonnelSearch.tsx